import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AppRepository } from '../api/repository';
import type { AuthSession, LoginChallenge, MfaMethod, RefreshResult, User } from '../api/models';

const LEGACY_IDB_NAME = 'bpmn-workspace';

/** Refresh the access cookie while this much lifetime remains. */
const REFRESH_IF_REMAINING_MS = 10 * 60 * 1000;

/** Tab-session dismissal of the expiring-license banner in the header. */
const LICENSE_NOTICE_DISMISSED_KEY = 'bpmn-license-notice-dismissed';

export function isLicenseNoticeDismissed(): boolean {
  try {
    return sessionStorage.getItem(LICENSE_NOTICE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissLicenseNotice(): void {
  try {
    sessionStorage.setItem(LICENSE_NOTICE_DISMISSED_KEY, '1');
  } catch {
    // Private mode can refuse storage. The header still hides the banner in memory.
  }
}

function clearLicenseNoticeDismissal(): void {
  try {
    sessionStorage.removeItem(LICENSE_NOTICE_DISMISSED_KEY);
  } catch {
    // Private mode can refuse storage.
  }
}

function isUnauthorized(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('status' in err)) return false;
  const status = (err as { status?: number }).status;
  return status === 401 || status === 403;
}

/** Drop tokens left in IndexedDB by the previous client. */
function purgeLegacyTokenStore(): Promise<void> {
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(LEGACY_IDB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Profile for templates. Tokens are not kept in script. */
  readonly user = signal<User | null>(null);
  /** False when the API has no share service configured. */
  readonly sharingEnabled = signal(false);
  readonly sharePublicBaseUrl = signal('');

  private csrfToken: string | null = null;
  private pendingChallenge: LoginChallenge | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Promise<boolean> | null = null;
  private logoutChannel: BroadcastChannel | null = null;

  constructor(
    private readonly repository: AppRepository,
    private readonly router: Router,
  ) {
    this.listenForRemoteLogout();
  }

  /**
   * Call from APP_INITIALIZER. Restores the cookie session and removes any
   * tokens previously stored in IndexedDB.
   */
  async initializeFromStorage(): Promise<void> {
    await purgeLegacyTokenStore();
    try {
      await this.loadCsrf();
    } catch {
      return;
    }
    try {
      this.applySession(await this.repository.session());
    } catch {
      this.clearLocalSession();
    }
  }

  get isLoggedIn(): boolean {
    return this.user() !== null;
  }

  getCsrfToken(): string | undefined {
    return this.csrfToken ?? undefined;
  }

  holdChallenge(challenge: LoginChallenge): void {
    this.pendingChallenge = challenge;
  }

  takeChallenge(): LoginChallenge | null {
    const challenge = this.pendingChallenge;
    this.pendingChallenge = null;
    return challenge;
  }

  loginWithGoogleIdToken(idToken: string): Promise<LoginChallenge> {
    return this.repository.authGoogle({ id_token: idToken });
  }

  /** Password check. A session is issued unless this account already enabled a second factor. */
  loginWithPassword(email: string, password: string) {
    return this.repository.loginWithPassword({ email, password });
  }

  async completeLoginMfa(mfaToken: string, method: MfaMethod, code: string): Promise<AuthSession> {
    const session = await this.repository.loginMfa({ mfa_token: mfaToken, method, code });
    this.applySession(session);
    return session;
  }

  sendLoginEmailCode(mfaToken: string): Promise<{ status: string }> {
    return this.repository.sendLoginEmailCode({ mfa_token: mfaToken });
  }

  async completeLoginPasskey(mfaToken: string, credential: Record<string, unknown>): Promise<AuthSession> {
    const session = await this.repository.loginWebauthnVerify({ mfa_token: mfaToken, credential });
    this.applySession(session);
    return session;
  }

  /** Store a session issued at the end of invite registration. */
  async establishSession(session: AuthSession): Promise<void> {
    this.applySession(session);
  }

  /**
   * GET /auth/me — updates `user` in memory.
   */
  async getMe(options?: Parameters<AppRepository['getMe']>[0]): Promise<User> {
    const user = await this.repository.getMe(options);
    this.user.set(user);
    return user;
  }

  async logout(): Promise<void> {
    const announce = this.user() !== null;
    try {
      await this.repository.logout();
    } catch {
      // The UI session is dropped either way. Cookies clear when the call succeeds.
    } finally {
      clearLicenseNoticeDismissal();
      this.clearLocalSession(announce);
    }
  }

  async deleteAccount(): Promise<void> {
    await this.repository.deleteAccount();
    this.clearLocalSession();
  }

  /**
   * Ask the API to rotate the access cookie. Returns false when the refresh
   * cookie is missing or rejected.
   */
  /** Re-read the signed-in user so role flags match the database. */
  async reloadSession(): Promise<void> {
    this.applySession(await this.repository.session());
  }

  refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async loadCsrf(): Promise<void> {
    const issued = await this.repository.csrf();
    this.csrfToken = issued.csrf_token;
  }

  private async performRefresh(): Promise<boolean> {
    const hadUser = this.user() !== null;
    try {
      const refreshed = await this.repository.refreshToken();
      this.applyRefresh(refreshed);
      return true;
    } catch (err) {
      if (isUnauthorized(err)) {
        this.clearLocalSession();
        if (hadUser) void this.router.navigateByUrl('/login');
      }
      return false;
    }
  }

  private applySession(session: AuthSession): void {
    this.csrfToken = session.csrf_token;
    this.user.set(session.user);
    this.sharingEnabled.set(session.sharing_enabled === true);
    this.sharePublicBaseUrl.set(session.share_public_base_url ?? '');
    this.scheduleRefresh(session.expires_in);
  }

  private applyRefresh(refreshed: RefreshResult): void {
    this.csrfToken = refreshed.csrf_token;
    this.scheduleRefresh(refreshed.expires_in);
  }

  private scheduleRefresh(expiresInSec: number): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    const delay = Math.max(1_000, expiresInSec * 1000 - REFRESH_IF_REMAINING_MS);
    this.refreshTimer = setTimeout(() => {
      void this.refreshSession();
    }, delay);
  }

  private listenForRemoteLogout(): void {
    if (typeof window === 'undefined') return;
    if (typeof BroadcastChannel !== 'undefined') {
      this.logoutChannel = new BroadcastChannel('bpmn-logout');
      this.logoutChannel.onmessage = () => this.onRemoteLogout();
    }
    window.addEventListener('storage', (event) => {
      if (event.key === 'bpmn-logout' && event.newValue) this.onRemoteLogout();
    });
  }

  private announceLogout(): void {
    try {
      this.logoutChannel?.postMessage({ type: 'logout' });
    } catch {
      // The storage write below still reaches other tabs.
    }
    try {
      localStorage.setItem('bpmn-logout', String(Date.now()));
    } catch {
      // Private mode can refuse storage. BroadcastChannel already ran.
    }
  }

  private onRemoteLogout(): void {
    if (this.user() === null) return;
    this.clearLocalSession(false);
    void this.router.navigateByUrl('/login?signedOut=1', { replaceUrl: true });
  }

  private clearLocalSession(announce = false): void {
    clearLicenseNoticeDismissal();
    this.user.set(null);
    this.sharingEnabled.set(false);
    this.sharePublicBaseUrl.set('');
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (announce) this.announceLogout();
  }
}

export function authInitializer(auth: AuthService) {
  return () => auth.initializeFromStorage();
}
